import { useMemo } from 'react'

import { resourceTag, resourceScopeTag, useWindow, useQueryResult } from '@krp/client-runtime'
import {
  PricingOverviewScreen,
  buildBulkArgs,
  buildDiscrepancyArgs,
  buildSaveFilterArgs,
  compareRequested,
  filterAttemptRecorded,
  firstConfigurationVersion,
  ONEC_PRICING_TAG,
  keysTextChanged,
  lookupRequested,
  makeSelectPricingOverviewVm,
  newCommandAttempt,
  onecDiscrepancies,
  onecConnectionForm,
  onecPriceFilter,
  onecPricesByRefKeys,
  onecSavePriceFilter,
  onecStockByRefKeys,
  pairChanged,
  pairDiscarded,
  pricingApi,
  type PricingOverviewActions,
} from '@krp/resource-onec-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { useResourceRevision } from '../platform/useResourceRevision'

/**
 * Binding окна «Цены и остатки»: rid — из окна, а не из глобальной настройки
 * (D-064). Все запросы несут rid; ревизия настроек базы для команд берётся
 * из ответов самой базы.
 */
export function PricingBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const resourceId = window.params.rid ?? ''
  const intent = useAppSelector((state) => state.onec.pricing)
  const comparing = intent.comparing[resourceId] ?? null

  const resourceArgs = useMemo(() => (resourceId === '' ? null : { resourceId }), [resourceId])
  const discrepancyArgs = useMemo(
    () => (resourceId === '' ? null : buildDiscrepancyArgs(resourceId, comparing)),
    [resourceId, comparing],
  )
  const filter = useQueryResult(onecPriceFilter, resourceArgs)
  const discrepancies = useQueryResult(onecDiscrepancies, discrepancyArgs)

  const version = firstConfigurationVersion(discrepancies, filter)
  const keys = intent.lookup[resourceId]
  const bulkArgs = useMemo(
    () =>
      version === null || keys === undefined || keys.length === 0
        ? null
        : { resourceId, refKeys: keys, configurationVersion: version },
    [resourceId, version, keys],
  )
  const stock = useQueryResult(onecStockByRefKeys, bulkArgs)
  const prices = useQueryResult(onecPricesByRefKeys, bulkArgs)

  const [saveFilter, save] = onecSavePriceFilter.useMutation({ fixedCacheKey: `${resourceId}:price-filter` })

  const resourceVersion = useResourceRevision(resourceId, onecConnectionForm.kind, 'prices.read')
  const selectVm = useMemo(() => makeSelectPricingOverviewVm(), [])
  const vm = selectVm({ resourceId, intent, filter, discrepancies, stock, prices, save, resourceVersion })

  const actions = useMemo<PricingOverviewActions>(
    () => ({
      reload: () => dispatch(pricingApi.util.invalidateTags([resourceScopeTag(resourceId)])),
      pairChanged: (field, value) =>
        dispatch(pairChanged({ resourceId, field, value, saved: filter.data?.data ?? null })),
      pairDiscarded: () => dispatch(pairDiscarded({ resourceId })),
      pairSaved: () => {
        const attempt = intent.filterAttempts[resourceId] ?? newCommandAttempt()
        const args = buildSaveFilterArgs(vm, attempt)
        if (args === null) return
        dispatch(filterAttemptRecorded({ resourceId, attempt }))
        void saveFilter(args)
      },
      compare: () => dispatch(compareRequested({ resourceId, pair: vm.filter.draft })),
      keysTextChanged: (text) => dispatch(keysTextChanged({ resourceId, text })),
      lookup: () => {
        const args = buildBulkArgs(vm, vm.lookup.keys)
        if (args === null) return
        dispatch(lookupRequested({ resourceId, keys: vm.lookup.keys }))
        // Тот же набор ключей — тот же ключ кэша: «Повторить» после отказа перечитывает явно.
        dispatch(
          pricingApi.util.invalidateTags([
            resourceTag(ONEC_PRICING_TAG, resourceId, 'STOCK:BULK'),
            resourceTag(ONEC_PRICING_TAG, resourceId, 'PRICES:BULK'),
          ]),
        )
      },
    }),
    [dispatch, resourceId, filter.data, intent.filterAttempts, saveFilter, vm],
  )
  return <PricingOverviewScreen vm={vm} actions={actions} />
}
